<template>
  <div class="TableContainer">
    <div ref="tableRef" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, defineExpose, getCurrentInstance } from "vue";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import api from "@/services/axios";
import { columnPresets } from "@/columns";

const instance = getCurrentInstance();
const emit = defineEmits(["select-item", "select-items", "data-loaded"]);

const props = defineProps({
  apiUrl: { type: String, default: null },
  data: { type: Array, default: null },
  tableType: { type: String, required: true },
  columns: { type: Array, required: true },
  pageSize: { type: Number, default: 10 },
  refreshFlag: { type: Number, default: 0 },
  filters: { type: Array, default: () => [] },
  height: { type: String, default: "400px" },
  // Nouvelle prop pour contrôler le mode de sélection
  selectionMode: { 
    type: String, 
    default: "single", 
    validator: (value) => ["single", "multi", "none"].includes(value)
  }
});

const tableRef = ref(null);
const table = ref(null);
const isLoading = ref(false);
const isInitialized = ref(false);

// Cache simple pour éviter les requêtes redondantes
const requestCache = new Map();

// 📦 fetchData : remote data loading (optimisé avec cache)
const fetchData = async (url, config, params) => {
  const page = params.page || 1;
  const size = params.size || props.pageSize;

  const headerFilters = table.value?.getHeaderFilters() || [];

  const searchParams = new URLSearchParams();
  searchParams.append("page", page);
  searchParams.append("size", size);

  if (props.filters?.length) {
    searchParams.append("filters", JSON.stringify(props.filters));
  }

  if (headerFilters.length) {
    searchParams.append("headerFilters", JSON.stringify(headerFilters));
  }

  if (Array.isArray(params.sorters) && params.sorters.length > 0) {
    searchParams.append("sortField", params.sorters[0].field);
    searchParams.append("sortDir", params.sorters[0].dir);
  }

  const fullUrl = `${props.apiUrl}?${searchParams.toString()}`;

  // Cache simplifié - seulement pour les requêtes sans filtres
  const cacheKey = fullUrl;
  const cached = requestCache.get(cacheKey);
  const hasFilters = (props.filters && props.filters.length > 0) || headerFilters.length > 0;
  
  if (cached && Date.now() - cached.timestamp < 5000 && !hasFilters) {
    console.log("📦 Cache hit for:", cacheKey);
    return cached.data;
  }

  try {
    const response = await api.get(fullUrl);
    const data = response.data;

    const listKey = {
      items: "items",
      groups: "groups",
      students: "students",
      tests: "tests",
      publications: "publications",
      results: "data",
    }[props.tableType] || "data";

    const result = {
      data: data[listKey] || [],
      last_page: data.last_page || data.totalPages || 1,
      totalItems: data.totalItems || data.total || 0,
    };

    // Mettre en cache le résultat
    requestCache.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });

    return result;
  } catch (err) {
    console.error("❌ Erreur fetchData :", err);
    return {
      data: [],
      last_page: 1,
      totalItems: 0,
    };
  }
};

onMounted(() => {
  if (!Array.isArray(props.columns)) {
    console.error("❌ Les colonnes ne sont pas un tableau :", props.columns);
    return;
  }

  // Créer une copie des colonnes en préservant les fonctions
  const nonReactiveColumns = props.columns.map(col => {
    // Créer une copie shallow de chaque colonne
    const newCol = { ...col };
    
    // Préserver les fonctions importantes
    if (typeof col.formatter === 'function') {
      newCol.formatter = col.formatter;
    }
    if (typeof col.cellClick === 'function') {
      newCol.cellClick = col.cellClick;
    }
    if (typeof col.titleFormatter === 'function') {
      newCol.titleFormatter = col.titleFormatter;
    }
    
    return newCol;
  });
  
  const tabulatorOptions = {
    layout: "fitDataFill", // Layout plus stable
    headerFilter: false, // Désactivé car nous utilisons la recherche globale
    columns: nonReactiveColumns, // Utiliser les colonnes non-réactives
    paginationSize: props.pageSize,
    paginationSizeSelector: [10, 20, 50],
    index: "_id",
    responsiveLayout: "hide",
    // Options de stabilité
    height: props.height,
    maxHeight: props.height,
  };

  // 🧠 Mode local (data fournie en prop)
  if (props.data) {
    tabulatorOptions.data = props.data;
    tabulatorOptions.pagination = true;
    tabulatorOptions.paginationMode = "local";
  }

  // 🌐 Mode distant (apiUrl fourni)
  else if (props.apiUrl) {
    tabulatorOptions.ajaxURL = props.apiUrl;
    tabulatorOptions.ajaxRequestFunc = fetchData;
    tabulatorOptions.pagination = true;
    tabulatorOptions.paginationMode = "remote";
    tabulatorOptions.ajaxConfig = "GET";
    tabulatorOptions.ajaxFiltering = true;
    tabulatorOptions.ajaxSorting = true;
    tabulatorOptions.filterMode = "remote";
    tabulatorOptions.sortMode = "remote";
  }

  // 🎯 Gestion de la sélection selon le mode
  if (props.selectionMode === "multi") {
    Object.assign(tabulatorOptions, {
      selectable: true,
      selectableRangeMode: "click",
      selectableRollingSelection: true,
      selectableRows: true,
    });
  } else if (props.selectionMode === "single") {
    Object.assign(tabulatorOptions, {
      selectable: 1,
      selectableRollingSelection: false,
    });
  } else {
    tabulatorOptions.selectable = false;
  }

  // Ajouter un rowFormatter pour gérer la surbrillance visuelle
  tabulatorOptions.rowFormatter = function(row) {
    const element = row.getElement();
    const isSelected = row.isSelected();

    if (isSelected) {
      element.classList.add("row-highlight");
    } else {
      element.classList.remove("row-highlight");
    }
  };

  // 🧱 Initialisation Tabulator avec protection contre les erreurs
  try {
    table.value = new Tabulator(tableRef.value, tabulatorOptions);
    table.value._component = instance?.proxy;

    table.value.on("tableBuilt", () => {
      isInitialized.value = true;
      console.log("✅ Table construite et initialisée");

      if (!props.data) {
        table.value.setData();
      }
    });

    table.value.on("dataLoaded", () => {
      console.log("✅ Données chargées, table prête.");
      emit("data-loaded");
    });

    table.value.on("error", (error) => {
      console.error("❌ Erreur Tabulator:", error);
      isLoading.value = false;
    });
  } catch (error) {
    console.error("❌ Erreur lors de l'initialisation de Tabulator:", error);
    isInitialized.value = false;
  }

  // Suppression du debouncing qui peut causer des conflits

  // 📦 Événements de sélection
  if (props.selectionMode === "single") {
  // Uniquement le clic déclenche la sélection
  table.value.on("rowClick", function (e, row) {
    // Désélectionner toutes les lignes d'abord
    table.value.deselectRow();

    // Retirer la classe row-highlight de toutes les lignes
    const allRows = table.value.getRows();
    allRows.forEach(r => {
      const element = r.getElement();
      if (element) {
        element.classList.remove("row-highlight");
      }
    });

    // Sélectionner la ligne cliquée
    row.select();
    const element = row.getElement();
    if (element) {
      element.classList.add("row-highlight");
    }

    const rowData = row.getData();
    emit("select-item", rowData);
  });
}

if (props.selectionMode === "multi") {
  table.value.on("rowSelectionChanged", function (data, rows) {
    const selectedData = rows.map((row) => row.getData());
    emit("select-items", selectedData);
  });
}


});


defineExpose({
  tableInstance: table.value,
  reloadData: () => {
    if (isLoading.value || !isInitialized.value || !table.value) return; // Éviter les appels multiples
    isLoading.value = true;
    requestCache.clear();
    table.value.setData()
      .then(() => {
        console.log("✅ Data reloaded");
        isLoading.value = false;
      })
      .catch(err => {
        console.error("❌ Reload error:", err);
        isLoading.value = false;
      });
  },
  getTable: () => table.value,
  // Méthodes utiles pour la sélection
  getSelectedRows: () => table.value?.getSelectedRows(),
  selectRow: (rowId) => table.value?.selectRow(rowId),
  deselectRow: () => table.value?.deselectRow(),
  toggleSelectRow: (rowId) => table.value?.toggleSelectRow(rowId),
  // Nouvelle méthode pour vider le cache
  clearCache: () => requestCache.clear()
});

watch(() => props.refreshFlag, () => {
  if (table.value && !isLoading.value && isInitialized.value) {
    isLoading.value = true;
    table.value.setData().finally(() => {
      isLoading.value = false;
    });
  }
});

watch(() => props.filters, (newFilters, oldFilters) => {
  // Optimisation : ne recharge que si les filtres ont vraiment changé
  if (table.value && !isLoading.value && isInitialized.value && JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
    console.log("🔄 Filtres changés, rechargement:", newFilters);
    isLoading.value = true;
    // Vider seulement une partie du cache
    if (props.apiUrl) {
      const baseUrl = props.apiUrl.split('?')[0];
      for (const [key] of requestCache.entries()) {
        if (key.startsWith(baseUrl)) {
          requestCache.delete(key);
        }
      }
    }
    table.value.setData().finally(() => {
      isLoading.value = false;
    });
  }
}, { deep: true });

watch(() => props.data, (newData) => {
  if (props.data && table.value && isInitialized.value) {
    table.value.replaceData(newData);
  }
});
</script>

<style scoped>
.TableContainer {
  width: 100%;
}
</style>

<style>
/* Boutons d'action modernes pour tous les tableaux */
.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #6c757d;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 2px;
}

.delete-btn:hover {
  background-color: #fff5f5;
  color: #dc3545;
  transform: scale(1.1);
}

.delete-btn:active {
  transform: scale(0.95);
}

.edit-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #6c757d;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 2px;
}

.edit-btn:hover {
  background-color: #e3f2fd;
  color: #1976D2;
  transform: scale(1.1);
}

.edit-btn:active {
  transform: scale(0.95);
}

/* Ligne sélectionnée avec trait de couleur à gauche */
.tabulator-row.tabulator-selected {
  background-color: #F3E5F5 !important;
  border-left: 5px solid #7B1FA2 !important;
  font-weight: 600 !important;
  box-shadow: inset 0 0 10px rgba(123, 31, 162, 0.1);
}

.tabulator-row.tabulator-selected:hover {
  background-color: #E1BEE7 !important;
}

/* Classe row-highlight pour la surbrillance des lignes sélectionnées */
.tabulator-row.row-highlight {
  background-color: #F3E5F5 !important;
  border-left: 5px solid #7B1FA2 !important;
  font-weight: 600 !important;
  box-shadow: inset 0 0 10px rgba(123, 31, 162, 0.1);
}

.tabulator-row.row-highlight:hover {
  background-color: #E1BEE7 !important;
}

/* Alternative pour les lignes sélectionnées dans les tableaux avec rowClick */
.tabulator-row.row-selected {
  background-color: #F3E5F5 !important;
  border-left: 5px solid #7B1FA2 !important;
  font-weight: 600 !important;
  box-shadow: inset 0 0 10px rgba(123, 31, 162, 0.1);
}

.tabulator-row.row-selected:hover {
  background-color: #E1BEE7 !important;
}
</style>